import { fetch_organization_dashboard } from '@/services/organization_service.js';

export default {
  name: 'OrganizationDashboard',

  data: () => ({
    dashboard: null,
    loading: false,
    error: null,
  }),

  mounted() {
    this.load_dashboard();
  },

  computed: {
    organization() {
      return this.dashboard?.organization || this.$store.getters.organization || {};
    },
    summary() {
      return this.dashboard?.summary || {};
    },
    configurations() {
      return this.dashboard?.configurations || {};
    },
    metric_cards() {
      return [
        {
          label: 'Projects',
          value: this.summary.projects || 0,
          meta: `${this.summary.active_projects || 0} active`,
          icon: 'bi-folder2-open',
          tone: 'primary',
          routeName: 'OrganizationProjects',
        },
        {
          label: 'Entity Configs',
          value: this.summary.entity_configs || 0,
          meta: `${this.summary.entity_types || 0} entity types`,
          icon: 'bi-diagram-3-fill',
          tone: 'success',
          routeName: 'EntityConfigs',
        },
        {
          label: 'Subsystem Bindings',
          value: this.subsystem_binding_total,
          meta: 'Derived from subsystem totals',
          icon: 'bi-link-45deg',
          tone: 'warning',
          routeName: 'EntityConfigs',
        },
        {
          label: 'Users',
          value: this.summary.users || 0,
          meta: `${this.summary.active_users || 0} active users`,
          icon: 'bi-people-fill',
          tone: 'info',
          routeName: 'OrganizationUsers',
        },
      ];
    },
    subsystem_binding_total() {
      return this.subsystem_breakdown.reduce((total, item) => total + Number(item.value || 0), 0);
    },
    subsystem_breakdown() {
      return [
        { label: 'Forms', value: this.summary.forms || 0, icon: 'bi-ui-checks-grid', routeName: 'CreateForm' },
        { label: 'Workflows', value: this.summary.workflows || 0, icon: 'bi-signpost-split-fill', routeName: 'CreateWorkflow' },
        { label: 'Questions', value: this.summary.questions || 0, icon: 'bi-question-square', routeName: 'ManageQuestions' },
        { label: 'Sections', value: this.summary.form_sections || 0, icon: 'bi-layout-text-window-reverse', routeName: 'CreateSections' },
        { label: 'Compliance Rules', value: this.summary.compliance_rules || 0, icon: 'bi-shield-check', routeName: 'ComplianceManagement' },
        { label: 'Risk Frameworks', value: this.summary.risk_frameworks || 0, icon: 'bi-exclamation-triangle', routeName: 'CreateRiskFramework' },
        { label: 'Document Templates', value: this.summary.document_templates || 0, icon: 'bi-file-earmark-check', routeName: 'ViewTemplate' },
        { label: 'Report Templates', value: this.summary.report_templates || 0, icon: 'bi-file-earmark-bar-graph', routeName: 'DynamicCaseReport' },
        { label: 'SLA Configs', value: this.summary.sla_configs || 0, icon: 'bi-clock-history', routeName: 'ServiceLayerAgreement' },
        { label: 'Notifications', value: this.summary.notification_configs || 0, icon: 'bi-bell', routeName: 'NotificationManagement' },
        { label: 'Audit Configs', value: this.summary.audit_configs || 0, icon: 'bi-clipboard-data', routeName: 'EntityConfigs' },
        { label: 'Statuses', value: this.summary.form_statuses || this.summary.statuses || this.summary.lifecycle_definitions || 0, icon: 'bi-tags-fill', routeName: 'ManageStatus' },
      ];
    },
    configuration_totals() {
      return [
        { label: 'Questions', value: this.summary.questions || 0 },
        { label: 'Sections', value: this.summary.form_sections || 0 },
        { label: 'Compliance Rules', value: this.summary.compliance_rules || 0 },
        { label: 'Risk Frameworks', value: this.summary.risk_frameworks || 0 },
        { label: 'Document Templates', value: this.summary.document_templates || 0 },
        { label: 'Report Templates', value: this.summary.report_templates || 0 },
        { label: 'SLA Configs', value: this.summary.sla_configs || 0 },
        { label: 'Notifications', value: this.summary.notification_configs || 0 },
        { label: 'Audit Configs', value: this.summary.audit_configs || 0 },
        { label: 'Statuses', value: this.summary.form_statuses || this.summary.statuses || this.summary.lifecycle_definitions || 0 },
      ];
    },
    recent_panels() {
      return [
        {
          title: 'Recent Entity Types',
          routeName: 'EntityConfigs',
          items: this.configurations.entity_types || [],
        },
        {
          title: 'Recent Entity Configs',
          routeName: 'EntityConfigs',
          items: this.configurations.entity_configs || [],
        },
        {
          title: 'Recent Forms',
          routeName: 'CreateForm',
          items: this.configurations.forms || [],
        },
        {
          title: 'Recent Workflows',
          routeName: 'CreateWorkflow',
          items: this.configurations.workflows || [],
        },
      ];
    },
    active_users() {
      return (this.dashboard?.recent_users || [])
        .filter((user) => user?.is_active !== false);
    },
    recent_projects() {
      return this.dashboard?.recent_projects || [];
    },
  },

  methods: {
    async load_dashboard() {
      this.loading = true;
      this.error = null;

      try {
        this.dashboard = await fetch_organization_dashboard();
        if (this.dashboard?.organization) {
          this.$store.dispatch('setOrganization', this.dashboard.organization);
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'Failed to load organization dashboard.';
      } finally {
        this.loading = false;
      }
    },
    item_name(item) {
      return item?.name || item?.project_name || item?.entity_type?.name || 'Untitled';
    },
    item_meta(item) {
      const project = item?.project?.name;
      const entityType = item?.entity_type?.name;
      const version = item?.version ? `Version ${item.version}` : '';
      return [project, entityType, version].filter(Boolean).join(' | ');
    },
    go_to_route(routeName) {
      if (!routeName) return;
      this.$router.push({ name: routeName });
    },
    go_to_entity_subsystem_bindings(item) {
      const entityTypeId = item?.entity_type_id || item?.id;
      if (!entityTypeId) {
        this.go_to_route('EntityConfigs');
        return;
      }
      this.$router.push({ name: 'EntitySubsystemBindings', params: { entityTypeId } });
    },
    go_to_metric(metric) {
      if (!metric?.routeName) return;
      this.go_to_route(metric.routeName);
    },
  },
};
